import React, { PropsWithChildren, useState, useMemo } from "react"
import { Slider as ArkSlider, SliderRootProps } from "@ark-ui/react"
import cx from "classnames"
import { FieldWrapper, FieldWrapperProps } from "./field"

const MIN_BAR_HEIGHT_PERCENT = 3

export type SliderPropsType<T extends number | number[]> = {
  /**
   * mandatory for A11y
   */
  value?: T
  defaultValue?: T
  ariaLabel: string
  /**
   * values interval
   */
  step?: number
  disabled?: boolean
  bars?: {
    step: number
    bins: Record<number, number>
  }
  /**
   * Listener called when the value is changed
   */
  onValueChange?: (newValue: T) => void
  /**
   * Function used to visually display the slider value
   */
  renderValue?: (
    value: T,
    status: FieldWrapperProps["status"]
  ) => React.ReactElement

  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
} & FieldWrapperProps &
  Omit<SliderRootProps, "value" | "defaultValue" | "onValueChange">

const Slider = <T extends number | number[]>({
  defaultValue: _defaultValue,
  value: _value,
  min,
  max,
  disabled,
  onValueChange,
  renderValue,
  ariaLabel,
  hint,
  label,
  status,
  className,
  step,
  bars,
  ...restProps
}: SliderPropsType<T>) => {
  const sliderMinValue = min || 0
  const sliderMaxValue = max || 100
  const value: number[] | undefined = Array.isArray(_value)
    ? _value
    : _value !== undefined && !isNaN(_value)
      ? [_value]
      : undefined
  const defaultValue: number[] | undefined = Array.isArray(_defaultValue)
    ? _defaultValue
    : _defaultValue !== undefined && !isNaN(_defaultValue)
      ? [_defaultValue]
      : undefined
  function initValue(): number[] {
    if (value !== undefined) return value
    if (defaultValue !== undefined) return defaultValue
    if (min !== undefined) return [min]
    return [0]
  }

  const [sliderValue, setSliderValue] = useState(() => initValue())

  const barsData = useMemo(() => {
    if (!bars || bars.step <= 0 || sliderMinValue >= sliderMaxValue) return null

    const barsCount = (sliderMaxValue - sliderMinValue) / bars.step

    let maxBars = 0
    const rawValues = []

    for (let i = 0; i < barsCount; i++) {
      const binValue = bars.bins[i * bars.step + sliderMinValue]
      if (binValue) {
        maxBars = Math.max(maxBars, binValue)
        rawValues.push(binValue)
      } else {
        rawValues.push(0)
      }
    }

    if (maxBars === 0) {
      return rawValues.map(() => 0) // all values are zero, so all bars are 0%
    }

    const scaleFactor = 100 - MIN_BAR_HEIGHT_PERCENT

    return rawValues.map((v) =>
      v > 0 ? (v / maxBars) * scaleFactor + MIN_BAR_HEIGHT_PERCENT : 0
    )
  }, [bars, sliderMinValue, sliderMaxValue])

  return (
    <FieldWrapper {...restProps} status={status} label={label} hint={hint}>
      <ArkSlider.Root
        {...restProps}
        className={cx("cobalt-slider", className, {
          "cobalt-slider--disabled": disabled,
          "cobalt-slider--withHint": hint,
          "cobalt-slider--withBars": !!bars,
        })}
        thumbAlignment="contain"
        disabled={disabled}
        aria-label={[ariaLabel]}
        value={sliderValue}
        min={min}
        max={max}
        step={step}
        // Need to force disbplaying in smaller spaces like popovers
        thumbSize={{
          width: 24,
          height: 24,
        }}
        onValueChange={({ value: newValueArr }) => {
          setSliderValue(newValueArr)
          const newValue =
            newValueArr.length === 1 ? newValueArr[0] : newValueArr
          onValueChange && onValueChange(newValue as T)
        }}
      >
        <ArkSlider.Label className={FieldWrapper.labelClassName}>
          {label}
        </ArkSlider.Label>
        {renderValue && (
          <div className="cobalt-slider__value-container">
            {renderValue(
              (sliderValue.length === 1 ? sliderValue[0] : sliderValue) as T,
              status
            )}
          </div>
        )}
        <ArkSlider.Control>
          <div className="cobalt-slider__bars">
            {barsData &&
              barsData.map((val, i) => {
                const barsStep = bars?.step || 1
                return (
                  <div
                    key={i}
                    className={cx("cobalt-slider__bar", {
                      "cobalt-slider__bar--active":
                        i * barsStep + sliderMinValue >= sliderValue[0] &&
                        (i + 1) * barsStep + sliderMinValue <= sliderValue[1],
                    })}
                    style={{
                      width: `calc(${100 / barsData.length}%)`,
                      height: `${val}%`,
                    }}
                  ></div>
                )
              })}
          </div>
          <ArkSlider.Track>
            <ArkSlider.Range />
          </ArkSlider.Track>
          {sliderValue.map((_v, i) => (
            <ArkSlider.Thumb key={i} index={i}>
              <ArkSlider.HiddenInput />
            </ArkSlider.Thumb>
          ))}
        </ArkSlider.Control>
      </ArkSlider.Root>
    </FieldWrapper>
  )
}

export default Slider

export const SliderValueMeta = ({ children }: PropsWithChildren) => {
  return <span className="cobalt-slider__value-meta">{children}</span>
}
